import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MagnifyingGlassIcon, PlusIcon, FunnelIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import TemplateGrid from './templates/TemplateGrid';
import TemplateGridSkeleton from './templates/TemplateGridSkeleton';
import TemplateEditorDrawer from './templates/TemplateEditorDrawer';
import TemplatePreview from './templates/TemplatePreview';
import { FormTemplate, FieldDefinition, TemplateFormData, FilterType, SortType } from './templates/types';

// Toast notification component
const Toast: React.FC<{ message: string; type: 'success' | 'error'; onClose: () => void }> = ({ message, type, onClose }) => (
  <div
    className="fixed top-4 right-4 z-50 animate-slideDown"
    style={{ animation: 'slideDown 0.3s ease-out' }}
  >
    <div className={`flex items-center px-6 py-3 rounded-lg shadow-lg ${type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
      <svg className={`w-5 h-5 mr-3 ${type === 'success' ? 'text-green-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {type === 'success' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        )}
      </svg>
      <p className={`text-sm font-medium ${type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message}</p>
      <button
        onClick={onClose}
        className={`ml-4 ${type === 'success' ? 'text-green-400 hover:text-green-600' : 'text-red-400 hover:text-red-600'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
);

interface FormTemplatesManagerProps {
  onClose: () => void;
  onTemplateUpdate?: () => void;
  isFullPage?: boolean;
}

const FormTemplatesManager: React.FC<FormTemplatesManagerProps> = ({ onClose, onTemplateUpdate, isFullPage = false }) => {
  const { user: _user } = useAuth();
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();

  // State
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [fieldDefinitions, setFieldDefinitions] = useState<{ [key: string]: FieldDefinition }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('active');
  const [sortType, setSortType] = useState<SortType>('name');
  const [showInactive, setShowInactive] = useState(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Auto-save state
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);

  // Form data
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    fieldsConfig: {
      fields: {}
    }
  });

  // Load templates and field definitions
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, fieldsRes] = await Promise.all([
        api.get('/leads/templates?includeInactive=true'),
        api.get('/leads/templates/field-definitions')
      ]);

      if (templatesRes.data.success) {
        setTemplates(templatesRes.data.data);
      }

      if (fieldsRes.data.success) {
        setFieldDefinitions(fieldsRes.data.data);

        // Initialize default field configuration
        const defaultFieldsConfig: { [key: string]: { visible: boolean; required: boolean; order: number } } = {};
        let order = 1;
        Object.keys(fieldsRes.data.data).forEach(fieldKey => {
          const field = fieldsRes.data.data[fieldKey];
          defaultFieldsConfig[fieldKey] = {
            visible: ['first_name', 'last_name', 'email', 'phone', 'alternative_mobile_number', 'gender', 'date_of_birth', 'company', 'segment_ids'].includes(fieldKey),
            required: field.required || false,
            order: order++
          };
        });

        setFormData(prev => ({
          ...prev,
          fieldsConfig: { fields: defaultFieldsConfig }
        }));
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!branchLoading) {
      if (isDrawerOpen) {
        closeDrawer();
      }
      loadData();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading, loadData]);

  // Auto-save draft to localStorage
  useEffect(() => {
    if (!hasUnsavedChanges || !isDrawerOpen) return;

    const autoSaveInterval = setInterval(() => {
      try {
        setAutoSaving(true);
        const draftKey = editingTemplate ? `template-draft-${editingTemplate.id}` : 'template-draft-new';
        localStorage.setItem(draftKey, JSON.stringify({
          formData,
          timestamp: new Date().toISOString()
        }));
        setLastAutoSave(new Date());
        setAutoSaving(false);
      } catch (error) {
        console.error('Auto-save failed:', error);
        setAutoSaving(false);
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [hasUnsavedChanges, isDrawerOpen, formData, editingTemplate]);

  // Load draft on mount
  useEffect(() => {
    if (isDrawerOpen) {
      const draftKey = editingTemplate ? `template-draft-${editingTemplate.id}` : 'template-draft-new';
      const savedDraft = localStorage.getItem(draftKey);
      
      if (savedDraft && !editingTemplate) {
        try {
          const { formData: savedFormData, timestamp } = JSON.parse(savedDraft);
          const savedDate = new Date(timestamp);
          const hoursSinceLastSave = (new Date().getTime() - savedDate.getTime()) / (1000 * 60 * 60);
          
          // Only restore if saved within last 24 hours
          if (hoursSinceLastSave < 24) {
            const shouldRestore = window.confirm(
              `Found an auto-saved draft from ${savedDate.toLocaleString()}. Would you like to restore it?`
            );
            
            if (shouldRestore) {
              setFormData(savedFormData);
              setHasUnsavedChanges(true);
              showToast('Draft restored successfully', 'success');
            } else {
              localStorage.removeItem(draftKey);
            }
          } else {
            localStorage.removeItem(draftKey);
          }
        } catch (error) {
          console.error('Failed to restore draft:', error);
        }
      }
    }
  }, [isDrawerOpen, editingTemplate]);

  // Toast helper
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Debounced search
  const debouncedSearch = useMemo(() => {
    let timeout: NodeJS.Timeout;
    return (value: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setSearchQuery(value), 300);
    };
  }, []);

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    let filtered = templates.filter(template => {
      // Filter by active/inactive
      if (filterType === 'active' && !template.isActive) return false;
      if (filterType === 'inactive' && template.isActive) return false;
      if (filterType === 'default' && !template.isDefault) return false;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return (
          template.name.toLowerCase().includes(query) ||
          template.description?.toLowerCase().includes(query) ||
          (template.creator_first_name?.toLowerCase() || '').includes(query) ||
          (template.creator_last_name?.toLowerCase() || '').includes(query)
        );
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortType) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'modified':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'usage':
          return (b.usageCount || 0) - (a.usageCount || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [templates, filterType, searchQuery, sortType]);

  const activeTemplates = filteredTemplates.filter(t => t.isActive);
  const inactiveTemplates = filteredTemplates.filter(t => !t.isActive);

  // Reset form
  const resetForm = () => {
    const defaultFieldsConfig: { [key: string]: { visible: boolean; required: boolean; order: number } } = {};
    let order = 1;
    Object.keys(fieldDefinitions).forEach(fieldKey => {
      const field = fieldDefinitions[fieldKey];
      defaultFieldsConfig[fieldKey] = {
        visible: ['first_name', 'last_name', 'email', 'phone', 'alternative_mobile_number', 'gender', 'date_of_birth', 'company', 'segment_ids'].includes(fieldKey),
        required: field.required || false,
        order: order++
      };
    });

    setFormData({
      name: '',
      description: '',
      fieldsConfig: { fields: defaultFieldsConfig }
    });
    setEditingTemplate(null);
    setHasUnsavedChanges(false);
  };

  // Open drawer for new template
  const openNewTemplate = () => {
    resetForm();
    setIsDrawerOpen(true);
  };

  // Open drawer for editing
  const openEditTemplate = (template: FormTemplate) => {
    setFormData({
      name: template.name,
      description: template.description || '',
      fieldsConfig: template.fieldsConfig
    });
    setEditingTemplate(template);
    setHasUnsavedChanges(false);
    setIsDrawerOpen(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => {
      resetForm();
    }, 300); // Wait for animation
  };

  // Handle form changes
  const handleFormChange = (data: Partial<TemplateFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
    setHasUnsavedChanges(true);
  };

  // Save template
  const saveTemplate = async () => {
    if (!formData.name.trim()) {
      showToast('Template name is required', 'error');
      return;
    }

    // Check for duplicates
    const duplicateTemplate = templates.find(
      t => t.name.toLowerCase() === formData.name.trim().toLowerCase() &&
        (!editingTemplate || t.id !== editingTemplate.id)
    );

    if (duplicateTemplate) {
      showToast(`A template with the name "${formData.name.trim()}" already exists`, 'error');
      return;
    }

    // Validate at least one field is visible
    const visibleFields = Object.values(formData.fieldsConfig?.fields || {}).filter(f => f.visible);
    if (visibleFields.length === 0) {
      showToast('At least one field must be visible', 'error');
      return;
    }

    // Normalize field order
    const normalizedFieldsConfig = { ...formData.fieldsConfig };
    const orderedFields = Object.entries(normalizedFieldsConfig.fields)
      .sort(([, a], [, b]) => a.order - b.order);

    orderedFields.forEach(([fieldKey, fieldConfig], index) => {
      normalizedFieldsConfig.fields[fieldKey] = {
        ...fieldConfig,
        order: index + 1
      };
    });

    setSaving(true);
    try {
      let response;

      if (editingTemplate) {
        response = await api.put(`/leads/templates/${editingTemplate.id}`, {
          ...formData,
          fieldsConfig: normalizedFieldsConfig
        });
      } else {
        response = await api.post('/leads/templates', {
          ...formData,
          fieldsConfig: normalizedFieldsConfig
        });
      }

      if (response.data.success) {
        // Clear auto-saved draft
        const draftKey = editingTemplate ? `template-draft-${editingTemplate.id}` : 'template-draft-new';
        localStorage.removeItem(draftKey);
        
        await loadData();
        closeDrawer();
        onTemplateUpdate?.();
        showToast(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!', 'success');
      } else {
        showToast(response.data.message || 'Failed to save template', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Error saving template', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const deleteTemplate = async (template: FormTemplate) => {
    if (template.isDefault) {
      showToast('Cannot delete the default template', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.delete(`/leads/templates/${template.id}`);
      if (response.data.success) {
        await loadData();
        onTemplateUpdate?.();
        showToast('Template deleted successfully!', 'success');
      } else {
        showToast(response.data.message || 'Failed to delete template', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Error deleting template', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Set default template
  const setDefaultTemplate = async (template: FormTemplate) => {
    if (!window.confirm(`Set "${template.name}" as the default template?`)) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/leads/templates/${template.id}/set-default`);
      if (response.data.success) {
        await loadData();
        onTemplateUpdate?.();
        showToast(`"${template.name}" is now the default template!`, 'success');
      } else {
        showToast(response.data.message || 'Failed to set default template', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Error setting default template', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Toggle template active status
  const toggleTemplateActive = async (template: FormTemplate) => {
    setSaving(true);
    try {
      const response = await api.put(`/leads/templates/${template.id}`, {
        isActive: !template.isActive
      });
      if (response.data.success) {
        await loadData();
        onTemplateUpdate?.();
        showToast(`Template ${!template.isActive ? 'activated' : 'deactivated'} successfully!`, 'success');
      } else {
        showToast(response.data.message || 'Failed to update template', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Error updating template', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Duplicate template
  const duplicateTemplate = (template: FormTemplate) => {
    setFormData({
      name: `${template.name} (Copy)`,
      description: template.description || '',
      fieldsConfig: template.fieldsConfig
    });
    setEditingTemplate(null);
    setHasUnsavedChanges(true);
    setIsDrawerOpen(true);
    showToast('Template ready to duplicate. Edit and save.', 'success');
  };

  // Loading state with skeleton
  const renderLoadingState = () => (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1">
            <div className="h-8 bg-gray-300 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 h-11 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="flex gap-2">
            <div className="h-11 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="h-11 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <TemplateGridSkeleton count={6} />
      </div>
    </div>
  );

  if (loading) {
    if (isFullPage) {
      return <div className="w-full h-full">{renderLoadingState()}</div>;
    }
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-7xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {renderLoadingState()}
        </div>
      </div>
    );
  }

  const content = (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Form Templates</h1>
            <p className="mt-1 text-sm text-gray-600">
              Create custom form templates to control field visibility and requirements
            </p>
          </div>
          {!isFullPage && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates by name, description, or creator..."
              onChange={(e) => debouncedSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Filter Chips */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filterType === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                All ({templates.length})
              </button>
              <button
                onClick={() => setFilterType('active')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filterType === 'active'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Active ({templates.filter(t => t.isActive).length})
              </button>
              <button
                onClick={() => setFilterType('default')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filterType === 'default'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Default
              </button>
            </div>

            {/* Add Template Button */}
            <button
              onClick={openNewTemplate}
              disabled={Object.keys(fieldDefinitions).length === 0}
              className="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Template
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {activeTemplates.length === 0 && filterType === 'active' && !searchQuery ? (
          <div className="text-center py-16">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No form templates yet</h3>
            <p className="mt-2 text-sm text-gray-600 max-w-sm mx-auto">
              Create your first form template to customize which fields appear when creating new leads.
            </p>
            <button
              onClick={openNewTemplate}
              className="mt-6 inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Your First Template
            </button>
          </div>
        ) : (
          <>
            <TemplateGrid
              templates={activeTemplates}
              onEdit={openEditTemplate}
              onDuplicate={duplicateTemplate}
              onDelete={deleteTemplate}
              onSetDefault={setDefaultTemplate}
              onToggleActive={toggleTemplateActive}
              saving={saving}
              emptyMessage={searchQuery ? `No templates found matching "${searchQuery}"` : 'No active templates'}
            />

            {/* Inactive Templates */}
            {inactiveTemplates.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowInactive(!showInactive)}
                  className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-4"
                >
                  <svg
                    className={`w-5 h-5 mr-2 transition-transform ${showInactive ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Inactive Templates ({inactiveTemplates.length})
                </button>

                {showInactive && (
                  <TemplateGrid
                    templates={inactiveTemplates}
                    onEdit={openEditTemplate}
                    onDuplicate={duplicateTemplate}
                    onDelete={deleteTemplate}
                    onSetDefault={setDefaultTemplate}
                    onToggleActive={toggleTemplateActive}
                    saving={saving}
                  />
                )}
              </div>
            )}
          </>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-sm font-medium text-blue-900 mb-3">💡 Tips for Managing Templates</h4>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• The default template is automatically used for new lead forms</li>
            <li>• Required fields must be visible to be enforced</li>
            <li>• At least one field must be visible in each template</li>
            <li>• Use different templates for different lead capture scenarios</li>
            <li>• Inactive templates are hidden from users but can be reactivated anytime</li>
          </ul>
        </div>
      </div>

      {/* Drawer */}
      <TemplateEditorDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        template={editingTemplate}
        formData={formData}
        onFormChange={handleFormChange}
        onSave={saveTemplate}
        onPreview={() => setShowPreview(true)}
        fieldDefinitions={fieldDefinitions}
        saving={saving}
        hasUnsavedChanges={hasUnsavedChanges}
        autoSaving={autoSaving}
        lastAutoSave={lastAutoSave}
      />

      {/* Preview Modal */}
      <TemplatePreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        formData={formData}
        fieldDefinitions={fieldDefinitions}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );

  if (isFullPage) {
    return <div className="w-full h-full">{content}</div>;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-7xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {content}
      </div>
    </div>
  );
};

export default FormTemplatesManager;
